/**
 * Typed access to Vite env vars. All client-exposed vars MUST start with `VITE_`.
 * Server-only secrets (Stripe secret, Cognito client secret) live in the Lambda,
 * never here.
 */

const required = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(`Missing required env var: ${name}`)
  }
  return value
}

export const env = {
  mode: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  cognito: {
    region: import.meta.env.VITE_COGNITO_REGION ?? 'eu-south-1',
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
    userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID ?? '',
  },

  stripe: {
    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
    demoPublishableKey: import.meta.env.VITE_STRIPE_DEMO_PUBLISHABLE_KEY ?? '',
  },

  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  },

  /**
   * Backend del flusso pubblico /preventivo. È diviso in due servizi:
   *
   *  - `ocrUrl`: API Gateway → Lambda OCR (DocuPipe). Espone
   *      POST /preventivo/submit  → { uid, status }
   *      GET  /preventivo/status/{uid} → { status, data? , error? }
   *    Pubblico (no Cognito), su account AWS dedicato.
   *
   *  - `apiUrl`: backend Odoo (tb-invoice-simulation). Espone
   *      POST /preventivo/confronto  (calcolo risparmio)
   *      POST /preventivo/contratto  (generazione contratto + firma)
   *
   * Separati da `api.baseUrl` (AppSync area cliente) perché pubblici e con
   * policy/infrastruttura proprie.
   */
  preventivo: {
    /**
     * Interruttore della pagina pubblica /preventivo. Disabilitato di default:
     * la rotta mostra una 404 finché non si imposta VITE_PREVENTIVO_ENABLED=true.
     * Permette di tenere il flusso offline finché non è pronto per il pubblico.
     */
    enabled: import.meta.env.VITE_PREVENTIVO_ENABLED === 'true',
    ocrUrl: import.meta.env.VITE_PREVENTIVO_OCR_URL ?? '',
    apiUrl: import.meta.env.VITE_PREVENTIVO_API_URL ?? '',
    /**
     * Endpoint di pricing dell'offerta proposta:
     *   GET /preventivo?offer_code=<code> → { pricePerKwh, pcvAnnual, punMeanAnnual, ... }
     * Usato per calcolare il confronto di risparmio lato frontend.
     */
    offerUrl: import.meta.env.VITE_PREVENTIVO_OFFER_URL ?? '',
  },

  /**
   * Contatti aziendali mostrati nella UI (numero verde, email assistenza, link
   * alle offerte). Configurabili via env; il fallback ai valori attuali evita
   * di doverli impostare in ogni ambiente.
   */
  contacts: {
    phone: import.meta.env.VITE_CONTACT_PHONE ?? '800616008',
    email: import.meta.env.VITE_CONTACT_EMAIL ?? 'servizioclienti@enermed.it',
    marketingUrl: import.meta.env.VITE_MARKETING_URL ?? 'https://enermed.it/offerta',
  },

  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  },

  /**
   * Google Analytics 4. `gaId` è il Measurement ID (formato G-XXXXXXX).
   * Vuoto di default: il tracking si attiva solo se valorizzato (e solo in
   * produzione, vedi src/lib/analytics.ts). In locale/demo resta no-op.
   */
  analytics: {
    gaId: import.meta.env.VITE_GA4_ID ?? '',
  },

  /**
   * Migrazione dominio: se valorizzato con l'URL del nuovo dominio
   * (es. https://areaclienti.enermed.it), l'app NON si avvia normalmente ma
   * mostra una pagina di avviso e reindirizza lì. Serve a tenere il vecchio
   * dominio (areaclienti.techus.it) come ponte temporaneo verso il nuovo,
   * finché tutti aggiornano il bookmark. Vuoto = app normale.
   */
  redirectTo: import.meta.env.VITE_REDIRECT_TO ?? '',
} as const

export { required }
